import React from 'react';
import { useTranslation } from 'react-i18next';

import { useReducedMotion } from '../../../../hooks/useReducedMotion';
import { IMPORTANT_NOW } from '../../../../theme/tokens';
import { useStripScroll } from '../ImportantNow/useStripScroll';
import { PickLine } from './PickLine';
import { SpotlightPick } from './SpotlightPick';
import {
  PickActions,
  PickCta,
  PickCtaLabel,
  PickDismiss,
  PickHeading,
  TITLE_LINE_PX,
  TITLE_LINES_VAR,
  TITLE_MAX_LINES,
  TitleSlot,
} from './SpotlightPick.styles';
import type { SpotlightCardItem, SpotlightItem, SpotlightProps } from './types';
import * as S from './Spotlight.styles';

/**
 * The header's light bulb, lit (the prototype's, the user's reference 2026-10-06; it replaced the warning octagon): a
 * glowing ring over a light grey base of bent bars. Its glow is the ring's own light, blurred (unlit in light).
 */
const SpotlightBulb = (): React.ReactElement => {
  const glow = `spotlight-bulb-glow-${React.useId().replace(/:/g, '')}`;
  return (
    <S.Bulb width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <defs>
        <filter id={glow} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="1.5" result="soft" />
          <feGaussianBlur in="SourceGraphic" stdDeviation="0.45" result="tight" />
          <feMerge>
            <feMergeNode in="soft" />
            <feMergeNode in="tight" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <path
        d="M6.5 13.65A6.8 6.8 0 1 1 17.5 13.65"
        stroke={IMPORTANT_NOW.bulbLight}
        strokeWidth="1.7"
        strokeLinecap="round"
        filter={`url(#${glow})`}
      />
      {/* The base: three bent bars, evenly spaced; the first is the ring's own circle closing under the gap. */}
      <path
        d="M8.397 15.417A6.8 6.8 0 0 0 15.603 15.417M9.493 18.814A9.5 9.5 0 0 0 14.507 18.814M11.5 21.84A12.2 12.2 0 0 0 12.5 21.84"
        stroke={IMPORTANT_NOW.bulbBase}
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </S.Bulb>
  );
};

/** The strip's arrows: right for next, mirrored for previous. */
const Chevron = ({ left = false }: { left?: boolean }): React.ReactElement => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path
      d={left ? 'M7.5 3L4.5 6L7.5 9' : 'M4.5 3L7.5 6L4.5 9'}
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const onKeyActivate = (action: () => void) => (event: React.KeyboardEvent<HTMLElement>) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    action();
  }
};

/** The card's buttons, as the pick's: the primary, then a quiet second. One verb: the CTA a span (the card is the
 *  control); two verbs: real buttons. */
const CardActions = ({ item }: { item: SpotlightCardItem }): React.ReactElement => {
  const { action, actions } = item;
  if (action) {
    return (
      <PickActions data-card="actions">
        <PickCta as="span">{action.cta}</PickCta>
      </PickActions>
    );
  }
  // Nothing to do: the row stays, empty, so the card keeps every card's height.
  if (!actions) return <S.NoActions data-card="actions" aria-hidden="true" />;
  const { primary, secondary } = actions;
  return (
    <PickActions data-card="actions">
      <PickCta type="button" onClick={primary.onClick}>
        <PickCtaLabel text={primary.label} raw />
      </PickCta>
      {secondary ? (
        <PickDismiss type="button" onClick={secondary.onClick}>
          <PickCtaLabel text={secondary.label} raw />
        </PickDismiss>
      ) : null}
    </PickActions>
  );
};

/**
 * One notice, in the pick's shape (the user, 2026-10-19): its eyebrow over its title, its line (three lines at most),
 * its buttons; its tone's glow over the card. With one verb the whole card is the control (its CTA a span), with two
 * its buttons are real.
 */
const SpotlightCard = ({ item }: { item: SpotlightCardItem }): React.ReactElement => {
  const { action } = item;
  return (
    <S.Card
      $tone={item.tone}
      $clickable={Boolean(action)}
      {...(action
        ? {
            role: 'button',
            tabIndex: 0,
            onClick: action.onActivate,
            onKeyDown: onKeyActivate(action.onActivate),
            'aria-label': action.ariaLabel,
          }
        : {})}
      data-testid={item.testId}
      data-fresh={item.fresh ? 'true' : undefined}
    >
      {item.backdrop}
      <S.CardContent>
        <PickHeading>
          {item.eyebrow}
          <TitleSlot data-card="title">{item.title}</TitleSlot>
        </PickHeading>
        {/* In a card that is itself the control, a cut line takes no focus: nothing nests in a button. */}
        <PickLine text={item.body.text} placeholders={item.body.placeholders} focusable={!action} />
        <CardActions item={item} />
      </S.CardContent>
    </S.Card>
  );
};

/** A card's title in its room (TitleSlot): the room's one child. */
const TITLE_SLOT = "[data-card='title']";

/**
 * Every card's title gets the room of the strip's longest (the user, 2026-10-23: two lines on every card once any title
 * wraps, else one, no blank line): the titles are measured as they lay out (the strip resized, the studio's font
 * arriving, another language) and the strip carries the count (TITLE_LINES_VAR).
 */
const useSharedTitleLines = (strip: React.RefObject<HTMLDivElement>, cards: string): void => {
  React.useLayoutEffect(() => {
    const root = strip.current;
    if (!root) return undefined;
    const titles = Array.from(root.querySelectorAll(TITLE_SLOT), (slot) => slot.firstElementChild).filter(
      (title): title is HTMLElement => title instanceof HTMLElement,
    );
    const measure = (): void => {
      const taken = titles.map((title) => Math.round(title.offsetHeight / TITLE_LINE_PX));
      root.style.setProperty(TITLE_LINES_VAR, String(Math.min(TITLE_MAX_LINES, Math.max(1, ...taken))));
    };
    measure();
    const observer = new ResizeObserver(measure);
    titles.forEach((title) => observer.observe(title));
    return () => observer.disconnect();
  }, [strip, cards]);
};

const renderItem = (item: SpotlightItem): React.ReactElement =>
  item.variant === 'pick' ? <SpotlightPick key={item.id} item={item} /> : <SpotlightCard key={item.id} item={item} />;

/** The section itself, mounted while it has cards (so it arrives, and leaves, with them). */
const SpotlightSection = ({ items, className }: SpotlightProps): React.ReactElement => {
  const { t } = useTranslation();
  const reducedMotion = useReducedMotion();
  // Read once, as the section arrives: it slides into place only when it comes with a fresh card. Later cards
  // arriving in it rise in on their own (Cards), the section stays put.
  const [enter] = React.useState(() => items.some((item) => item.fresh === true));
  // The arrows appear only when there is more to scroll to; paging moves one card and stops at the ends, the arrow
  // there disabled.
  // It opens on the first card whenever the cards change (a card arriving at the front included).
  const { ref, canScroll, atStart, atEnd, scrollLeft, scrollRight } = useStripScroll(items.length, items[0]?.id);
  useSharedTitleLines(ref, items.map((item) => item.id).join(' '));
  return (
    <S.Root className={className} aria-label={t('Important notifications')} $enter={enter}>
      {/* Lumio's surface: the aurora drifting under frosted glass. */}
      <S.RootAurora reduced={reducedMotion} />
      <S.RootGlass aria-hidden="true" />
      <S.Header>
        <S.HeaderLeft>
          <SpotlightBulb />
          <S.HeaderLabel text="Spotlight" />
          {items.length > 1 ? <S.HeaderCount aria-hidden="true">{items.length}</S.HeaderCount> : null}
        </S.HeaderLeft>
        {canScroll ? (
          <S.HeaderNav>
            <S.NavButton type="button" onClick={scrollLeft} aria-label={t('Scroll to previous')} disabled={atStart}>
              <Chevron left />
            </S.NavButton>
            <S.NavButton type="button" onClick={scrollRight} aria-label={t('Scroll to next')} disabled={atEnd}>
              <Chevron />
            </S.NavButton>
          </S.HeaderNav>
        ) : null}
      </S.Header>
      <S.Cards
        ref={ref}
        $fadeLeft={canScroll && !atStart}
        $fadeRight={canScroll && !atEnd}
        data-testid="important-now-strip"
      >
        {items.map(renderItem)}
      </S.Cards>
    </S.Root>
  );
};

/**
 * Home's Spotlight, the ows-ftux prototype's (its aurora under frosted glass, its card sizes, its pick, its entrance):
 * the header (a lit bulb, "Spotlight", a count past one card, arrows when the strip overflows) and a snapping strip of
 * cards, Lumio's pick among them. Renders nothing without items. A card marked `fresh` rises in with a short, quiet
 * entrance; the section slides into place when it arrives with one (CSS keyframes, so Home ships no GSAP).
 */
export const Spotlight = ({ items, className }: SpotlightProps): React.ReactElement | null =>
  items.length === 0 ? null : <SpotlightSection items={items} className={className} />;

export default Spotlight;
